import { Aide } from '@/components/ui/aide';
import React, { useState } from 'react';

import { maintenanceAPI } from '../../lib/api';
import { messageErreur } from '../../lib/erreurs';
import { effectiveTruckStatus } from '../../lib/truckStatus';
import { useAuth } from '../../context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../ui/sonner';

// Les rôles que l'API maintenance laisse immobiliser et lever
// (`_WRITE_ROLES` de backend/app/api/v1/maintenance.py).
export const IMMOBILIZATION_ROLES = ['SUPER_ADMIN', 'ADMIN_SOCIETE', 'LOGISTIQUE', 'MAINTENANCE'];

/**
 * Immobiliser / lever, depuis la fiche camion.
 *
 * C'est sur le camion qu'on décide de l'arrêter, pas dans l'écran atelier.
 * L'immobilisation est le seul blocage réel d'affectation: le statut est
 * recalculé côté serveur, on le relit après coup (`onChanged`) plutôt que de
 * le deviner ici.
 *
 * `immobilization` (celle du profil) évite une recherche; sans elle, on
 * retrouve l'immobilisation active par le camion.
 */
export default function TruckImmobilizationActions({
  truck,
  immobilization = null,
  onChanged,
  className,
}) {
  const { user, hasModule } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!truck || !hasModule('maintenance')) return null;
  if (!IMMOBILIZATION_ROLES.includes(user?.role)) return null;

  const status = effectiveTruckStatus(truck);
  const technicalMaintenance =
    truck.technical_status === 'MAINTENANCE' || truck.technical_status === 'BROKEN';
  const immobilized =
    Boolean(immobilization) ||
    status === 'IMMOBILIZED' ||
    (status === 'MAINTENANCE' && !technicalMaintenance);

  const immobilize = async () => {
    setBusy(true);
    try {
      await maintenanceAPI.immobilize({
        truck_id: truck.id,
        reason: reason.trim() || 'Immobilisation manuelle',
        reason_code: 'manual',
      });
      toast.success('Camion immobilisé');
      setOpen(false);
      setReason('');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(messageErreur(err, 'Impossible d’immobiliser le camion'));
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    setBusy(true);
    try {
      let record = immobilization;
      if (!record?.id) {
        const active = await maintenanceAPI.getImmobilizations({
          truck_id: truck.id,
          status: 'active',
        });
        record = (Array.isArray(active) ? active : active?.items || [])[0];
      }
      if (!record?.id) {
        toast.error('Aucune immobilisation active à lever sur ce camion');
        return;
      }
      await maintenanceAPI.releaseImmobilization(record.id, 'Levée depuis la fiche camion');
      toast.success('Immobilisation levée');
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(messageErreur(err, 'Impossible de lever l’immobilisation'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      {immobilized ? (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={busy}
          onClick={release}
          data-testid="truck-release-immobilization"
        >
          Lever l'immobilisation
        </Button>
      ) : technicalMaintenance ? (
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          Ce camion est déclaré en maintenance technique.
          <Aide titre="Maintenance technique">
            Il n’est pas disponible pour une affectation.
          </Aide>
        </p>
      ) : (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          disabled={busy}
          onClick={() => setOpen(true)}
          data-testid="truck-immobilize"
        >
          Immobiliser
        </Button>
      )}

      {/* Le motif est saisi, jamais codé en dur: c'est lui que l'atelier
          lira dans la liste des immobilisés. */}
      <AlertDialog
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            setOpen(false);
            setReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Immobiliser {truck.matricule}</AlertDialogTitle>
            <AlertDialogDescription>
              Le camion est refusé à l'affectation tant que l'immobilisation n'est pas
              levée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label htmlFor="immobilization-reason">Motif</Label>
            <Input
              id="immobilization-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Ex. fuite sur la citerne"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={immobilize}>
              {busy ? 'Immobilisation…' : 'Immobiliser'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
